import type { Node } from "@op/richtext-types";
import { lazy, useEffect, useId } from "react";

export interface NodeRendererProps {
  node: Node;
}

const HeadingNode = lazy(() => import("./nodes/HeadingNode"));
const TextNode = lazy(() => import("./nodes/TextNode"));
const ParagraphNode = lazy(() => import("./nodes/ParagraphNode"));
const OrderedListNode = lazy(() => import("./nodes/OrderedListNode"));
const ListItemNode = lazy(() => import("./nodes/ListItemNode"));
const BulletListNode = lazy(() => import("./nodes/BulletListNode"));
const TaskListNode = lazy(() => import("./nodes/TaskListNode"));
const TaskItemNode = lazy(() => import("./nodes/TaskItemNode"));
const BlockquoteNode = lazy(() => import("./nodes/BlockquoteNode"));
const CodeBlockNode = lazy(() => import("./nodes/CodeBlockNode"));
const HardBreakNode = lazy(() => import("./nodes/HardBreakNode"));
const CitationNode = lazy(() => import("./nodes/CitationNode"));

const NullNode = ({ node, id }: { node: Node; id?: string }) => {
  useEffect(() => {
    console.log({ unknownNode: node });
  }, []);

  return (
    <div id={`${node.type}-${id}`}>
      <code>Unknown Node type: {node.type}</code>
      <br />
    </div>
  );
};

export const NodeRenderer = ({ node }: NodeRendererProps) => {
  const id = useId();

  switch (node.type) {
    case "heading":
      return <HeadingNode {...node} id={id} />;
    case "text":
      return <TextNode {...node} id={id} />;
    case "paragraph":
      return <ParagraphNode {...node} id={id} />;
    case "orderedList":
      return <OrderedListNode {...node} id={id} />;
    case "bulletList":
      return <BulletListNode {...node} id={id} />;
    case "listItem":
      return <ListItemNode {...node} id={id} />;
    case "taskList":
      return <TaskListNode {...node} id={id} />;
    case "taskItem":
      return <TaskItemNode {...node} id={id} />;
    case "blockquote":
      return <BlockquoteNode {...node} id={id} />;
    case "codeBlock":
      return <CodeBlockNode {...node} id={id} />;
    case "hardBreak":
      return <HardBreakNode {...node} id={id} />;
    case "citation":
      return <CitationNode {...node} id={id} />;
    default:
      return <NullNode node={node} id={id} />;
  }
};
